import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRefCallback } from '@okee-uikit/react-hooks';
import { guid } from '@okee-uikit/react-utils';
import { usePrefixCls } from '@okee-uikit/react-config';
import { Down, Up } from '../../../common/icons';
import { HotKeyContext, useCreateHotKey } from '../../../hooks/hotkey';
import { useSelect } from '../../../hooks/select';
import { RcPopover } from '../../../units/popover-wrapper';
import { SelectHotKey } from '../../util/SelectHotKey';
import { useSelectPopper } from '../hooks/useSelectPopper';
import { SelectInput } from './SelectInput';
export function SelectPopper(props) {
  var _props$searchable = props.searchable,
      searchable = _props$searchable === void 0 ? false : _props$searchable,
      _props$invalid = props.invalid,
      invalid = _props$invalid === void 0 ? false : _props$invalid,
      _props$placement = props.placement,
      placement = _props$placement === void 0 ? 'bottom-start' : _props$placement,
      autoAlign = props.autoAlign,
      autoFocusSearch = props.autoFocusSearch,
      placeholder = props.placeholder,
      onShow = props.onShow,
      onHide = props.onHide,
      show = props.show,
      popoverWidth = props.popoverWidth,
      children = props.children,
      emptyRender = props.emptyRender,
      emptyContent = props.emptyContent,
      popoverClassName = props.popoverClassName,
      popoverWrapperClassName = props.popoverWrapperClassName,
      popoverPreventOverflow = props.popoverPreventOverflow,
      onSearchChange = props.onSearchChange,
      searchPlaceholder = props.searchPlaceholder,
      renderSelected = props.renderSelected,
      renderPlaceholder = props.renderPlaceholder,
      boundariesDetectionDebounce = props.boundariesDetectionDebounce,
      getPopupContainer = props.getPopupContainer,
      _props$filled = props.filled,
      filled = _props$filled === void 0 ? false : _props$filled,
      prefix = props.prefix,
      renderFooter = props.renderFooter,
      renderIcon = props.renderIcon,
      onVisibleChange = props.onVisibleChange;
  var inputRef = useRef(null);
  var select = useSelect();
  var triggerId = useMemo(function () {
    return guid();
  }, []);
  var triggerIds = useMemo(function () {
    return [triggerId];
  }, []);
  var hotKey = useCreateHotKey(inputRef);
  var selectArrowCls = usePrefixCls('select-arrow');
  var handleClear = useCallback(function (val) {
    if (!val) {
      select.resetValue();
    }
  }, []);
  var handleShow = useRefCallback(function () {
    onVisibleChange === null || onVisibleChange === void 0 ? void 0 : onVisibleChange(true);
    onShow === null || onShow === void 0 ? void 0 : onShow();
  });
  var handleHide = useRefCallback(function () {
    onVisibleChange === null || onVisibleChange === void 0 ? void 0 : onVisibleChange(false);
    onHide === null || onHide === void 0 ? void 0 : onHide();
  });
  useEffect(function () {
    select.addListener('try-set-state', handleHide);
    return function () {
      select.removeListener('try-set-state', handleHide);
    };
  }, []);
  var suffix = useMemo(function () {
    if (renderIcon) {
      return renderIcon(show);
    }

    return show ? React.createElement(Up, {
      className: selectArrowCls
    }) : React.createElement(Down, {
      className: selectArrowCls
    });
  }, [show, renderIcon]);

  var _useSelectPopper = useSelectPopper({
    show: show,
    searchable: searchable,
    autoAlign: autoAlign,
    autoFocusSearch: autoFocusSearch,
    popoverWidth: popoverWidth,
    children: children,
    emptyContent: emptyContent,
    emptyRender: emptyRender,
    triggerId: triggerId,
    onSearchChange: onSearchChange,
    searchPlaceholder: searchPlaceholder,
    renderFooter: renderFooter
  }),
      getPopover = _useSelectPopper.getPopover,
      displayInfo = _useSelectPopper.displayInfo,
      size = _useSelectPopper.size,
      clearable = _useSelectPopper.clearable;

  return React.createElement(HotKeyContext.Provider, {
    value: hotKey
  }, React.createElement(SelectHotKey, {
    focusableElementRef: inputRef,
    handlers: select
  }), React.createElement(SelectInput, {
    show: show,
    clearable: clearable,
    inputRef: inputRef,
    value: displayInfo.length && displayInfo[0].label || '',
    displayInfo: displayInfo,
    placeholder: placeholder,
    renderSelected: renderSelected,
    renderPlaceholder: renderPlaceholder,
    size: size,
    disabled: select.disabled,
    readOnly: select.readonly,
    htmlReadOnly: true,
    invalid: invalid,
    onValueChange: handleClear,
    triggerId: triggerId,
    prefix: prefix,
    suffix: suffix,
    filled: filled
  }), React.createElement(RcPopover, {
    show: show,
    getPopper: getPopover,
    arrow: false,
    placement: placement,
    offset: {
      x: 0,
      y: 4
    },
    triggerIds: triggerIds,
    onShow: handleShow,
    onHide: handleHide,
    scaleAnimation: "y",
    lazyInit: false,
    className: popoverClassName,
    wrapperClassName: popoverWrapperClassName,
    boundariesDetectionDebounce: boundariesDetectionDebounce,
    getPopupContainer: getPopupContainer,
    preventOverflow: popoverPreventOverflow,
    defaultToBody: true
  }));
}